/**
 * @fileoverview Ad Spends container with filters, platform toggles, table and pagination.
 * @module features/ad-spends/components/AdSpendsComponent
 */

import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useFilter } from '@/shared/hooks/useFilter'
import { adService } from '@/features/ad-spends/api/adService'
import { type AdSpendDTO, type Platform } from '@/features/ad-spends/schemas/adSpendsSchema'
import { Button } from '@/shared/components/ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/shared/components/ui'

/**
 * Column definition for table sorting.
 */
interface Column {
  key: keyof AdSpendDTO | 'currencyCode'
  label: string
}

/**
 * Render-friendly currency as code string from API field that can be object or string.
 */
function getCurrencyCode(currency: AdSpendDTO['currency']): string {
  if (typeof currency === 'string') return currency
  if (currency && typeof currency === 'object' && 'currencyCode' in currency) {
    const value = (currency as { currencyCode?: string }).currencyCode
    return value ?? ''
  }
  return ''
}

/**
 * AdSpendsComponent: orchestrates filters, data fetching, sorting and pagination.
 */
/**
 * AdSpendsComponent export (named) for router/page composition.
 */
export function AdSpendsComponent({ platforms }: { platforms: Platform[] }): ReactElement {
  const { t } = useTranslation('features-ad-spends')

  // Pagination/sort state
  const [page, setPage] = useState<number>(0)
  const [size, setSize] = useState<number>(50)
  const [sort, setSort] = useState<string>('date')
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc')

  // Data state
  const [adSpends, setAdSpends] = useState<AdSpendDTO[]>([])
  const [totalPages, setTotalPages] = useState<number>(0)
  const [totalElements, setTotalElements] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Filters from shared provider
  const { startDate, endDate, countries, skus } = useFilter()

  // Platform toggles are provided by the parent page via props

  const columns: Column[] = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'date', label: t('table.columns.date') },
      { key: 'platform', label: t('table.columns.platform') },
      { key: 'country', label: t('table.columns.country') },
      { key: 'sku', label: t('table.columns.sku') },
      { key: 'amount', label: t('table.columns.amount') },
      { key: 'currencyCode', label: t('table.columns.currency') },
    ],
    [t]
  )

  const handleSortChange = (column: string): void => {
    if (sort === column) {
      setDirection(direction === 'asc' ? 'desc' : 'asc')
    } else {
      setSort(column)
      setDirection('asc')
    }
  }

  const getSortIndicator = (column: string): string => {
    if (sort !== column) return ''
    return direction === 'asc' ? ' ↑' : ' ↓'
  }

  const pageSizes = [10, 25, 50, 100]

  const fetchAdSpends = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Narrow countries (string[]) to Country[] by filtering to allowed enum values
      const allowedCountries = [
        'PL',
        'CZ',
        'SK',
        'HU',
        'RO',
        'BG',
        'HR',
        'SI',
        'LT',
        'LV',
        'EE',
        'IE',
        'SE',
        'DE',
        'AT',
        'NL',
        'FR',
        'IT',
        'PT',
        'UNKNOWN',
      ] as const
      type AllowedCountry = (typeof allowedCountries)[number]

      const countriesParam =
        Array.isArray(countries) && countries.length > 0
          ? (countries.filter((c): c is AllowedCountry =>
              (allowedCountries as readonly string[]).includes(c)
            ) as AllowedCountry[])
          : undefined

      const res = await adService.getAdSpends({
        page,
        size,
        sort,
        direction,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
        skus: skus && skus.length > 0 ? skus : undefined,
        countries: countriesParam,
        platforms: platforms.length > 0 ? platforms : undefined,
      })
      setAdSpends(res.content)
      setTotalPages(res.totalPages)
      setTotalElements(res.totalElements)
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t('messages.error')
      setError(message)
      setAdSpends([])
      setTotalPages(0)
      setTotalElements(0)
    } finally {
      setLoading(false)
    }
  }, [page, size, sort, direction, startDate, endDate, skus, countries, platforms, t])

  useEffect(() => {
    void fetchAdSpends()
  }, [fetchAdSpends])

  const getPageNumbers = (): number[] => {
    const list: number[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i += 1) list.push(i)
    } else if (page < 3) {
      for (let i = 0; i < 4; i += 1) list.push(i)
      // ellipsis simulated by -1 sentinel handled in render
      list.push(-1, totalPages - 1)
    } else if (page >= totalPages - 3) {
      list.push(0, -1)
      for (let i = totalPages - 4; i < totalPages; i += 1) list.push(i)
    } else {
      list.push(0, -1, page - 1, page, page + 1, -1, totalPages - 1)
    }
    return list
  }

  return (
    <div className="flex flex-col flex-1">
      {/* Platform controls are injected into the global FlyingMenu slot */}

      {/* Ad Spends Table */}
      <div className="max-w-full overflow-x-auto flex-1">
        {loading ? (
          <div className="p-8">
            <div className="flex flex-col gap-3">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-destructive">{error}</div>
        ) : adSpends.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">{t('messages.noData')}</div>
        ) : (
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                {columns.map((c, i) => (
                  <TableHead
                    key={c.key as string}
                    className={`pl-6 cursor-pointer text-xs font-medium text-muted-foreground uppercase tracking-wide ${i > 0 ? 'border-l border-border/40' : ''}`}
                    onClick={() =>
                      handleSortChange(c.key === 'currencyCode' ? 'currency' : (c.key as string))
                    }
                    aria-label={t('table.actions.sortBy', { column: c.label })}
                  >
                    <span>
                      {c.label}
                      {getSortIndicator(c.key === 'currencyCode' ? 'currency' : (c.key as string))}
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {adSpends.map(row => (
                <TableRow key={row.id}>
                  <TableCell className="pl-6 text-xs text-muted-foreground select-none">
                    {row.id}
                  </TableCell>
                  <TableCell className="pl-6 border-l border-border/40">
                    {format(new Date(row.date), 'dd LLL yyyy')}
                  </TableCell>
                  <TableCell className="pl-6 text-muted-foreground border-l border-border/40">
                    {row.platform}
                  </TableCell>
                  <TableCell className="pl-6 text-muted-foreground border-l border-border/40">
                    {row.country}
                  </TableCell>
                  <TableCell className="pl-6 text-muted-foreground border-l border-border/40">
                    {row.sku?.name ?? ''}
                  </TableCell>
                  <TableCell className="pl-6 text-muted-foreground border-l border-border/40">
                    {row.amount}
                  </TableCell>
                  <TableCell className="pl-6 text-muted-foreground border-l border-border/40">
                    {getCurrencyCode(row.currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && adSpends.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border sm:px-6 gap-4">
          <div className="flex items-center">
            <p className="text-sm text-muted-foreground">
              {t('pagination.showing', {
                from: page * size + 1,
                to: Math.min((page + 1) * size, totalElements),
                total: totalElements,
              })}
            </p>
          </div>

          {/* Page size selector */}
          <div className="flex items-center gap-2">
            <label htmlFor="page-size" className="text-sm text-muted-foreground">
              {t('pagination.rowsPerPage')}:
            </label>
            <select
              id="page-size"
              value={size}
              onChange={e => {
                const newSize = Number.parseInt(e.target.value, 10)
                setSize(newSize)
                setPage(0)
              }}
              className="rounded-md border bg-background py-1.5 px-3 text-sm text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {pageSizes.map(ps => (
                <option key={ps} value={ps}>
                  {ps}
                </option>
              ))}
            </select>
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              {t('pagination.previous')}
            </Button>

            {getPageNumbers().map((n, idx) =>
              n === -1 ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">
                  ...
                </span>
              ) : (
                <Button
                  key={n}
                  size="sm"
                  variant={n === page ? 'default' : 'outline'}
                  onClick={() => setPage(n)}
                >
                  {n + 1}
                </Button>
              )
            )}

            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
